import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiErr } from "../lib/api";
import { useLang } from "../lib/i18n";
import { PageHeader } from "../lib/ui";
import { Button } from "../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { FilePlus2, FileText, Download, Trash2, Camera, Printer, Archive, ArchiveRestore } from "lucide-react";
import { usePdfPreview } from "../components/PdfPreview";

const VOLET_LABEL = { epi: "Épidémiologiste", lab: "Laboratoire", generic: "Autre pilier" };
const VOLET_CLS = { epi: "bg-[#FFF3EE] text-[#F05A28] border-[#FDBA88]", lab: "bg-sky-50 text-sky-700 border-sky-200", generic: "bg-slate-100 text-slate-600 border-slate-200" };

const fetchPdf = (id) => api.get(`/monthly-reports/${id}/pdf`, { responseType: "blob" }).then((r) => r.data);

function downloadPdf(id) {
  fetchPdf(id).then((b) => {
    const url = URL.createObjectURL(b); const a = document.createElement("a");
    a.href = url; a.download = "Rapport_Mensuel.pdf"; a.click(); URL.revokeObjectURL(url);
  });
}

export default function MonthlyReports() {
  const { t } = useLang();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [archives, setArchives] = useState(false);
  const pdf = usePdfPreview();
  const { data: reports, isLoading } = useQuery({
    queryKey: ["monthly-reports", archives],
    queryFn: async () => (await api.get("/monthly-reports", { params: archives ? { archived: true } : {} })).data,
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ["monthly-reports"] });

  const del = async (id) => {
    if (!window.confirm(t("Supprimer ce rapport ?"))) return;
    try { await api.delete(`/monthly-reports/${id}`); toast.success(t("Rapport supprimé.")); refresh(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const toggleArchive = async (r) => {
    try {
      await api.post(`/monthly-reports/${r.id}/archive`, { archived: !r.archived });
      toast.success(t(r.archived ? "Rapport restauré." : "Rapport archivé.")); refresh();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const preview = (r) => pdf.show(() => fetchPdf(r.id), { title: `${t("Rapport mensuel")} · ${r.month} ${r.year}`, filename: `Rapport_Mensuel_${r.month}_${r.year}.pdf` })
    .catch((e) => toast.error(apiErr(e.response?.data?.detail)));

  return (
    <div data-testid="monthly-reports-page">
      <PageHeader eyebrow="Riposte MVE · Experts de terrain" title="Rapport Mensuel" subtitle="Rapports mensuels structurés des épidémiologistes, laboratoires et autres piliers">
        <Button className="rounded-md bg-[#F05A28] hover:bg-[#D84A1B]" data-testid="mr-new" onClick={() => navigate("/monthly/new")}>
          <FilePlus2 className="h-4 w-4 mr-1.5" /> {t("Nouveau rapport mensuel")}
        </Button>
      </PageHeader>

      <div className="flex gap-2 mb-4">
        {[{ k: false, label: "En cours" }, { k: true, label: "Archives", icon: Archive }].map((x) => (
          <button key={String(x.k)} onClick={() => setArchives(x.k)} data-testid={`mr-filter-${x.k ? "archives" : "current"}`}
            className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors inline-flex items-center gap-1.5 ${archives === x.k ? "bg-[#0F172A] text-white" : "bg-white border border-[#E2E8F0] text-slate-600 hover:bg-slate-50"}`}>
            {x.icon && <x.icon className="h-3.5 w-3.5" />}{t(x.label)}
          </button>
        ))}
      </div>

      {isLoading ? <p className="text-slate-400 text-sm">{t("Chargement…")}</p> : (
        (reports || []).length === 0 ? (
          <div className="bg-white border border-[#E2E8F0] rounded-lg shadow-sm p-12 text-center" data-testid="mr-empty">
            <FileText className="h-9 w-9 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">{t(archives ? "Aucun rapport mensuel archivé." : "Aucun rapport mensuel pour le moment.")}</p>
          </div>
        ) : (
          <div className="bg-white border border-[#E2E8F0] rounded-lg shadow-sm overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F1F5F9]">
                  <TableHead>{t("Période")}</TableHead><TableHead>{t("Volet")}</TableHead>
                  <TableHead>{t("Auteur")}</TableHead><TableHead>{t("Province / Zones")}</TableHead>
                  <TableHead>{t("Statut")}</TableHead><TableHead className="text-right">{t("Actions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(reports || []).map((r) => (
                  <TableRow key={r.id} className="hover:bg-[#F8FAFC]" data-testid={`mr-row-${r.id}`}>
                    <TableCell className="font-semibold text-slate-800 cursor-pointer" onClick={() => navigate(`/monthly/${r.id}`)}>
                      {r.month} {r.year} {r.report_no ? `· N°${r.report_no}` : ""}
                      {r.photo_count > 0 && <span className="ml-2 inline-flex items-center text-[11px] text-slate-500"><Camera className="h-3 w-3 mr-0.5" />{r.photo_count}</span>}
                    </TableCell>
                    <TableCell><span className={`inline-flex px-2.5 py-0.5 rounded text-xs font-semibold border ${VOLET_CLS[r.volet] || VOLET_CLS.generic}`}>{t(VOLET_LABEL[r.volet] || r.volet)}</span></TableCell>
                    <TableCell className="text-sm text-slate-600">{r.author_name}</TableCell>
                    <TableCell className="text-sm text-slate-600">{r.province}{r.health_zones ? ` · ${r.health_zones}` : ""}</TableCell>
                    <TableCell><span className="text-xs text-slate-500">{r.status === "submitted" ? t("Soumis") : t("Brouillon")}</span></TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button size="sm" variant="ghost" onClick={() => preview(r)} data-testid={`mr-preview-${r.id}`} title={t("Aperçu avant impression")}><Printer className="h-4 w-4" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => downloadPdf(r.id)} data-testid={`mr-pdf-${r.id}`}><Download className="h-4 w-4" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => toggleArchive(r)} data-testid={`mr-archive-${r.id}`} title={t(r.archived ? "Restaurer" : "Archiver")}>
                        {r.archived ? <ArchiveRestore className="h-4 w-4 text-emerald-700" /> : <Archive className="h-4 w-4 text-slate-500" />}
                      </Button>
                      <Button size="sm" variant="ghost" className="text-red-600" onClick={() => del(r.id)} data-testid={`mr-del-${r.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )
      )}
      {pdf.el}
    </div>
  );
}
